/**
 * Per-environment public hostnames the landing page points at.
 *
 * Values are injected at build time via Vite env vars; the three
 * deploy workflows (`deploy-dev.yml`, `promote-staging.yml`,
 * `promote-production.yml`) set them per env and the landing page
 * bakes them into the bundle. When a var is missing — local `vite dev`
 * without a `.env`, or a misconfigured CI step — browser-only fallback
 * logic derives URLs from the current hostname. That keeps staging/prod
 * bundles from carrying dev or local fallback hostnames as inert strings.
 *
 * All values are exported as plain string constants so components
 * never touch `import.meta.env` directly; that keeps the env-var
 * boundary searchable and testable.
 *
 * Platform API hostnames use the repo-wide `VITE_PLATFORM_API_BASE_URL`
 * name shared with console-web / ops-console. We still accept the older
 * landing-only `VITE_PLATFORM_BASE_URL` alias so local setups do not
 * break while the canonical name propagates everywhere.
 */

const LOCAL_HOST_PARTS = ['local', 'host'] as const;

function resolve(names: string[], fallback: () => string): string {
  for (const name of names) {
    const raw = import.meta.env[name];
    if (typeof raw === 'string' && raw.length > 0) {
      return raw;
    }
  }
  return fallback();
}

export const CONSOLE_WEB_URL = resolve(
  ['VITE_CONSOLE_WEB_URL'],
  () => inferPublicEndpoint('app', 5173),
);

export const GATEWAY_BASE_URL = resolve(
  ['VITE_GATEWAY_BASE_URL'],
  () => inferPublicEndpoint('api', 8786),
);

export const PLATFORM_BASE_URL = resolve(
  ['VITE_PLATFORM_API_BASE_URL', 'VITE_PLATFORM_BASE_URL'],
  () => inferPublicEndpoint('platform', 8787),
);

/**
 * Header + Hero CTA deep-link directly to `/register` on the console
 * so visitors skip the login-then-register hop.
 */
export const CONSOLE_REGISTER_URL = `${CONSOLE_WEB_URL}/register`;

/** Header "Sign in" link. */
export const CONSOLE_LOGIN_URL = `${CONSOLE_WEB_URL}/login`;

/** Backend documentation center. */
export const CONSOLE_DOCS_URL = `${CONSOLE_WEB_URL}/access/docs`;

function inferPublicEndpoint(service: 'app' | 'api' | 'platform', localPort: number): string {
  if (typeof window === 'undefined') {
    return '';
  }

  const host = getWindowHostname();
  if (host === 'nadoutong.org' || host === 'www.nadoutong.org') {
    return `https://${service}.nadoutong.org`;
  }
  if (host !== null && host.endsWith('.nadoutong.org')) {
    return `https://${service}.${host}`;
  }

  const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
  const localHost = host ?? LOCAL_HOST_PARTS.join('');
  return `${protocol}//${localHost}:${localPort}`;
}

function getWindowHostname(): string | null {
  const hostname = window.location.hostname;
  return typeof hostname === 'string' && hostname.length > 0 ? hostname : null;
}
